import React, { useMemo, useState } from 'react';
import { clsx, colorSet } from '@components/Provider';
import { Chip, Divider, Grid, IconButton } from '@material-ui/core';
import useStyles from './useStyles';

import {
  CustomizedButton,
  CustomizedTooltip,
  CustomizedTypography
} from '@components/Customized';

import {
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';

import { requestEnumToString } from '@utils/enumToString';

import { Check, ChevronRightOutlined, InfoOutlined } from '@material-ui/icons';
import { UploadedDrawingFileType } from '@modules/request';

import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import { Close, Information } from '@carbon/icons-react';
import { DialogTypes } from './types';
import howToContents from './Dialogs/HowtoModal/HowToContents';

interface Props {
  matches: boolean;
  serviceType?: CapaQuotationServiceType;
  detailedServiceType?: CapaQuotationDetailedServiceType;
  uploadedDrawingFiles: UploadedDrawingFileType[];
  onOpenMdGuidDialog: () => void;
  onOpenDialog: (type: keyof DialogTypes) => void;
}

const RequestForQuotationHeader = ({
  matches,
  serviceType,
  detailedServiceType,
  uploadedDrawingFiles = [],
  onOpenMdGuidDialog,
  onOpenDialog
}: Props) => {
  const [open, setOpen] = useState(false);

  const [guideToolTipOpen, setGuideToolTipOpen] = useState(true);

  const classes = useStyles();

  const isEmpty = uploadedDrawingFiles.length === 0;

  const hasHowToModal =
    serviceType && Object.keys(howToContents).includes(serviceType);

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_NO_DRAWING_FILE;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_NO_DRAWING_FILE;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_NO_DRAWING_FILE;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_NO_DRAWING_FILE;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_NO_DRAWING_FILE;
    }
  }, [serviceType]);

  return (
    <section className={clsx('rfq-header', classes['rfq-info-section'])}>
      <Chip
        className="rfq-header-servicetype-chip"
        label={
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            wrap={matches ? 'nowrap' : 'wrap'}
          >
            <Grid item>
              <Check fontSize="small" color="primary" />
              <CustomizedTypography
                fontWeight="bold"
                variant={matches ? 'caption' : 'body2'}
                color="textPrimary"
              >
                {requestEnumToString(serviceType)}
              </CustomizedTypography>
            </Grid>
            <Divider orientation="vertical" />
            <Grid item>
              <Check fontSize="small" color="primary" />

              <CustomizedTypography
                fontWeight="bold"
                variant={matches ? 'caption' : 'body2'}
                color="textPrimary"
              >
                {requestEnumToString(detailedServiceType).split('(')[0]}
              </CustomizedTypography>
            </Grid>
          </Grid>
        }
        clickable={false}
      />

      <CustomizedTypography
        className="title"
        align="center"
        color="textPrimary"
        gutterBottoms={8}
      >
        도면 업로드 후 재료와 수량을 입력해주세요
      </CustomizedTypography>

      {hasHowToModal && (
        <CustomizedTooltip
          open={guideToolTipOpen}
          backgroundcolor={colorSet.primary500}
          arrow
          title={
            <Grid container alignItems="center">
              <CustomizedTypography variant="caption" color="initial">
                가이드라인을 읽고 작성해주시면
                <br />
                더욱 정확한 견적을 받을 수 있습니다.
              </CustomizedTypography>
              <IconButton onClick={() => setGuideToolTipOpen(false)}>
                <Close size={20} color={colorSet.gray000} />
              </IconButton>
            </Grid>
          }
        >
          <Chip
            className="rfq-header-guide-chip"
            icon={<Information size={14} color={colorSet.gray700} />}
            label={
              <CustomizedTypography variant="body2" color="textPrimary">
                정확한 견적을 받는 법
              </CustomizedTypography>
            }
            clickable
            onClick={() => onOpenDialog('howTo')}
          />
        </CustomizedTooltip>
      )}

      {isEmpty && (
        <CustomizedTooltip
          className="rfq-header-tooltip"
          arrow={true}
          title={
            <CustomizedTypography
              variant="body2"
              color="inherit"
              fontWeight="normal"
            >
              기계 부품, 금형 설계 등 실제 제조를 위한 상세 도면을 제작합니다.
              CAPA 비교 견적을 이용하고 싶은데 도면이 없으시다면 이 서비스를
              진행해 보세요.
            </CustomizedTypography>
          }
          open={open}
          onClick={() => setOpen(!open)}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
        >
          <CustomizedButton
            logId={logId}
            variant="text"
            color="primary"
            startIcon={<InfoOutlined className="startIcon" />}
            endIcon={<ChevronRightOutlined color="primary" />}
            onClick={() => {
              onOpenMdGuidDialog();
            }}
          >
            도면이 없다면?
          </CustomizedButton>
        </CustomizedTooltip>
      )}
    </section>
  );
};

export default React.memo(RequestForQuotationHeader);
